import { useState, useEffect } from 'react';
import { t } from '../i18n';
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, getDay, addMonths, subMonths, addDays, isToday, isBefore, isAfter, startOfWeek, endOfWeek, addWeeks, startOfDay } from 'date-fns';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// Japanese public holidays (fixed + calculated moveable holidays)
const JP_HOLIDAYS = new Set([
  // 2025
  '2025-01-01','2025-01-13','2025-02-11','2025-02-23','2025-02-24',
  '2025-03-20','2025-04-29','2025-05-03','2025-05-04','2025-05-05',
  '2025-05-06','2025-07-21','2025-08-11','2025-09-15','2025-09-23',
  '2025-10-13','2025-11-03','2025-11-23','2025-11-24',
  // 2026
  '2026-01-01','2026-01-12','2026-02-11','2026-02-23','2026-03-20',
  '2026-04-29','2026-05-03','2026-05-04','2026-05-05','2026-05-06',
  '2026-07-20','2026-08-11','2026-09-21','2026-09-22','2026-09-23',
  '2026-10-12','2026-11-03','2026-11-23',
  // 2027
  '2027-01-01','2027-01-11','2027-02-11','2027-02-23','2027-03-22',
  '2027-04-29','2027-05-03','2027-05-04','2027-05-05','2027-07-19',
  '2027-08-11','2027-09-20','2027-09-23','2027-10-11','2027-11-03',
  '2027-11-23',
]);

const MIN_MONTH = new Date(2026, 5, 1); // June 2026
const MAX_MONTH = new Date(2026, 8, 1); // September 2026

export default function LunchCalendar({ selected = [], onChange, bookedMatches = [] }) {
  const today = startOfDay(new Date());
  // Matching runs every Monday at noon JST for the following week.
  // Before noon JST on Monday, next week is still editable; after noon it's locked.
  const nowJST = new Date(Date.now() + 9 * 60 * 60 * 1000);
  const isBeforeNoonMondayJST = nowJST.getUTCDay() === 1 && nowJST.getUTCHours() < 12;
  const thisWeekSunday = endOfWeek(startOfWeek(today, { weekStartsOn: 1 }), { weekStartsOn: 1 });
  const nextWeekSunday = endOfWeek(addWeeks(startOfWeek(today, { weekStartsOn: 1 }), 1), { weekStartsOn: 1 });
  const minSelectableDate = isBeforeNoonMondayJST ? thisWeekSunday : nextWeekSunday;
  // Default the visible month to the one containing the first day that can
  // actually be picked, instead of always the current month — otherwise,
  // near a month boundary, the calendar can open showing only locked days.
  const [viewDate, setViewDate] = useState(() => addDays(minSelectableDate, 1));
  const [popup, setPopup] = useState(null); // { date, participants, top, left }

  const monthStart = startOfMonth(viewDate);
  const monthEnd = endOfMonth(viewDate);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
  // Monday-start: Sun(0)→6, Mon(1)→0, Tue(2)→1, ...
  const startPad = (getDay(monthStart) + 6) % 7;

  const fmt = d => format(d, 'yyyy-MM-dd');

  // Build a map of date -> participants for O(1) lookup
  const bookedMap = new Map(
    bookedMatches.filter(bm => bm.date).map(bm => [bm.date, bm.participants])
  );

  const isWeekendOrHoliday = (day) => {
    const dow = getDay(day); // 0=Sun, 6=Sat
    if (dow === 0 || dow === 6) return true;
    return JP_HOLIDAYS.has(fmt(day));
  };

  const handleDayClick = (day, e) => {
    const key = fmt(day);
    if (bookedMap.has(key)) {
      e.stopPropagation();
      const rect = e.currentTarget.getBoundingClientRect();
      setPopup({ date: key, participants: bookedMap.get(key), top: rect.bottom + 8, left: rect.left });
      return;
    }
    if (!isAfter(day, minSelectableDate)) return;
    if (isWeekendOrHoliday(day)) return;
    const next = selected.includes(key)
      ? selected.filter(d => d !== key)
      : [...selected, key];
    onChange(next);
  };

  useEffect(() => {
    if (!popup) return;
    const close = () => setPopup(null);
    const onKey = (e) => { if (e.key === 'Escape') setPopup(null); };
    document.addEventListener('click', close);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [popup]);

  return (
    <div>
      {/* Month nav */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <button className="btn btn-ghost" style={{ padding: '6px 10px', visibility: startOfMonth(viewDate) <= MIN_MONTH ? 'hidden' : undefined }} onClick={() => setViewDate(subMonths(viewDate, 1))}>
          ←
        </button>
        <span style={{ fontFamily: 'Playfair Display', fontSize: 18, fontWeight: 600 }}>
          {format(viewDate, 'MMMM yyyy')}
        </span>
        <button className="btn btn-ghost" style={{ padding: '6px 10px', visibility: startOfMonth(viewDate) >= MAX_MONTH ? 'hidden' : undefined }} onClick={() => setViewDate(addMonths(viewDate, 1))}>
          →
        </button>
      </div>

      {/* Header */}
      <div className="calendar-grid" style={{ marginBottom: 8 }}>
        {DAYS.map(d => <div key={d} className="cal-header">{d}</div>)}
      </div>

      {/* Grid */}
      <div className="calendar-grid">
        {Array.from({ length: startPad }).map((_, i) => <div key={`pad-${i}`} />)}
        {days.map(day => {
          const key = fmt(day);
          const isPast = isBefore(day, today);
          const isLocked = !isPast && !isAfter(day, minSelectableDate);
          const isWH = isWeekendOrHoliday(day);
          const isDisabled = isPast || isLocked || isWH;
          const isSel = selected.includes(key);
          const isTodayDay = isToday(day);
          const isBooked = bookedMap.has(key);
          const isHoliday = !isWH ? false : JP_HOLIDAYS.has(key);
          const isWeekend = !isWH ? false : (getDay(day) === 0 || getDay(day) === 6);
          return (
            <div
              key={key}
              className={`cal-day ${isSel ? 'selected' : ''} ${isDisabled ? 'disabled' : ''} ${isWH ? 'weekend-holiday' : ''} ${isTodayDay && !isSel ? 'today' : ''}`}
              onClick={(e) => handleDayClick(day, e)}
              title={
                isBooked
                  ? `Lunch booked on ${format(day, 'MMM d')} — click for details`
                  : isWH
                    ? isHoliday && !isWeekend
                      ? 'Japanese public holiday'
                      : isHoliday
                        ? 'Weekend / Japanese public holiday'
                        : 'Weekend'
                  : isPast
                    ? 'Past date'
                    : isLocked
                      ? 'Locked — matching runs Monday at noon JST'
                      : `${isSel ? 'Remove' : 'Add'} ${format(day, 'MMM d')}`
              }
              style={{ cursor: isBooked ? 'pointer' : undefined }}
            >
              {format(day, 'd')}
              {isBooked && <span className="booked-dot" />}
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 14, display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12, color: 'var(--text-muted)' }}>
        {(() => { const future = selected.filter(d => isAfter(startOfDay(new Date(d + 'T12:00:00')), today)); return future.length > 0 && (
          <span>{future.length} day{future.length !== 1 ? 's' : ''} marked available</span>
        ); })()}
        {bookedMatches.length > 0 && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--match-a)', display: 'inline-block' }} />
            {bookedMatches.length} lunch{bookedMatches.length !== 1 ? 'es' : ''} booked
          </span>
        )}
      </div>
      <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>
        {t('calendarLockNote')}
      </div>

      {/* Popup for booked date details */}
      {popup && (
        <div
          onClick={e => e.stopPropagation()}
          style={{
            position: 'fixed',
            top: popup.top,
            left: popup.left,
            zIndex: 1000,
            background: 'white',
            border: '1.5px solid var(--border)',
            borderRadius: 12,
            padding: '16px 20px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
            minWidth: 220,
            maxWidth: 300
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 12, fontSize: 14, color: 'var(--match-a)' }}>
            🍵 {format(parseISO(popup.date), 'EEEE, MMMM d')}
          </div>
          {popup.participants.map(p => (
            <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <div className="avatar" style={{ background: p.color, width: 34, height: 34, fontSize: 14, flexShrink: 0 }}>
                {p.name[0]}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{p.name}</div>
                <a href={`mailto:${p.email}`}
                  style={{ fontSize: 12, color: 'var(--match-a)', textDecoration: 'none', wordBreak: 'break-all' }}
                  onMouseOver={e => e.currentTarget.style.textDecoration = 'underline'}
                  onMouseOut={e => e.currentTarget.style.textDecoration = 'none'}>
                  {p.email}
                </a>
              </div>
            </div>
          ))}
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Click outside to close</div>
        </div>
      )}
    </div>
  );
}
